import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { Lock, Sparkles, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { useEntitlements } from '@/contexts/EntitlementsContext';
import { useAuth } from '@/contexts/AuthContext';
import { FeatureKey } from '@/services/billingApi';

export const FEATURE_METADATA: Record<
  FeatureKey,
  { name: string; requiredPlan: string; priceText: string; description?: string }
> = {
  pos: {
    name: 'Point of Sale',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Fast barcode scanning, cashier registers, and in-person checkout.',
  },
  inventory: {
    name: 'Inventory Management',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Stock tracking, low stock alerts, and automated stock counts.',
  },
  expenses: {
    name: 'Expense Tracking',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Track operational costs, upload receipts, and categorize spend.',
  },
  basicReports: {
    name: 'Basic Reports',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Daily sales, top-selling products, and revenue summaries.',
  },
  sync: {
    name: 'Offline Sync',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Keep operating and selling even when the internet goes down.',
  },
  exports: {
    name: 'Data & CSV Exports',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Export sales, orders, products, and customers to CSV and spreadsheets.',
  },
  purchaseOrders: {
    name: 'Purchase Orders & Stock Reordering',
    requiredPlan: 'Shop Plus',
    priceText: '£39/mo',
    description: 'Automated supplier orders, stock replenishment, and delivery receipts.',
  },
  onlineStore: {
    name: 'Online Storefront & Catalog',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Your own branded online store for customer pickup and delivery.',
  },
  advancedReports: {
    name: 'Advanced Profit & Loss, Staff & Tax Reports',
    requiredPlan: 'Shop Plus',
    priceText: '£39/mo',
    description: 'Deep financial analytics, margins, gross profit, and employee stats.',
  },
  onlineCheckout: {
    name: 'Online Customer Checkout & Payments',
    requiredPlan: 'Starter',
    priceText: '£19/mo',
    description: 'Accept customer card payments directly through your digital storefront.',
  },
  payroll: {
    name: 'UK Payroll & Statutory Compliance',
    requiredPlan: 'Business Hub',
    priceText: '£69/mo',
    description: 'Automated PAYE calculations, payslips, pension contributions, and tax codes.',
  },
  rota: {
    name: 'Staff Rota, Shifts & Availability',
    requiredPlan: 'Business Hub',
    priceText: '£69/mo',
    description: 'Smart staff scheduling, shift assignment, and real-time attendance.',
  },
  vatReturns: {
    name: 'Accounting & HMRC VAT Submissions',
    requiredPlan: 'Business Hub',
    priceText: '£69/mo',
    description: 'Making Tax Digital (MTD) VAT returns, ledger reconciliation, and tax filings.',
  },
  foodSafety: {
    name: 'Food Safety & Hygiene Logs',
    requiredPlan: 'Business Hub',
    priceText: '£69/mo',
    description: 'Daily fridge/freezer checks, cook & cool logs, and opening/closing compliance.',
  },
  training: {
    name: 'Staff Training & Compliance Courses',
    requiredPlan: 'Business Hub',
    priceText: '£69/mo',
    description: 'Interactive staff lessons, quizzes, and verifiable food hygiene certificates.',
  },
};

export interface UpgradePlanCardProps {
  feature?: FeatureKey;
  title?: string;
  description?: string;
  requiredPlan?: string;
  priceText?: string;
  onUpgrade?: () => void;
  className?: string;
}

/**
 * HeroUI Card displaying "This feature is not available in your current plan" with an Upgrade Plan CTA.
 */
export const UpgradePlanCard: React.FC<UpgradePlanCardProps> = ({
  feature,
  title,
  description,
  requiredPlan,
  priceText,
  onUpgrade,
  className = '',
}) => {
  const { user } = useAuth();
  const { openUpgradeModal } = useEntitlements();
  const navigate = useNavigate();

  const meta = feature ? FEATURE_METADATA[feature] : undefined;
  const planName = requiredPlan || meta?.requiredPlan || 'Shop Plus';
  const planPrice = priceText || meta?.priceText || '£39/mo';
  const featureTitle = title || (meta ? `${meta.name}` : 'This feature');
  const featureDesc =
    description ||
    meta?.description ||
    'Upgrade your subscription plan to unlock access to this tool and expand your business capabilities.';

  const isOwner = user?.role === 'OWNER';

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      openUpgradeModal({ feature, title: featureTitle, requiredPlan: planName });
    }
  };

  return (
    <div className={`min-h-[460px] w-full flex items-center justify-center p-6 ${className}`}>
      <Card
        shadow="none"
        className="max-w-md w-full border border-gray-200/90 rounded-2xl bg-white p-2 shadow-sm animate-in fade-in zoom-in-95 duration-200"
      >
        <CardHeader className="flex flex-col items-center text-center pt-6 pb-2 px-6">
          <div className="size-14 rounded-2xl bg-amber-50 border border-amber-200/70 flex items-center justify-center text-amber-600 mb-4 shadow-sm">
            <Lock size={26} strokeWidth={2.2} />
          </div>

          <Chip
            variant="solid"
            color="warning"
            size="sm"
            className="font-semibold text-xs text-white mb-3 px-3 py-1"
          >
            Requires {planName} Plan ({planPrice})
          </Chip>

          <h2 className="text-lg font-bold text-gray-900 tracking-tight leading-snug">
            This feature is not available in your current plan
          </h2>
        </CardHeader>

        <CardFooter className="pt-2 pb-6 px-6 flex flex-col gap-2">
          {isOwner ? (
            <Button
              color="primary"
              size="lg"
              className="w-full bg-[#172e50] hover:bg-[#1a3359] text-white font-semibold rounded-full text-sm transition-all shadow-sm"
              onPress={handleUpgrade}
            >
              Upgrade Plan
            </Button>
          ) : (
            <div className="w-full p-3 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-600 flex items-center justify-center gap-2">
              <ShieldAlert size={16} className="text-gray-400 shrink-0" />
              <span>Please ask your business account owner to upgrade the plan.</span>
            </div>
          )}
           
        </CardFooter>
      </Card>
    </div>
  );
};

export { UpgradePlanModal, type UpgradePlanModalProps } from './UpgradePlanModal';

export interface FeatureGateProps {
  feature: FeatureKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  title?: string;
  description?: string;
}

/**
 * FeatureGate wraps children and renders UpgradePlanCard using HeroUI when the feature is locked.
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback,
  title,
  description,
}) => {
  const { hasFeature } = useEntitlements();

  const isAllowed = hasFeature(feature);

  if (isAllowed) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return <UpgradePlanCard feature={feature} title={title} description={description} />;
};

export const PlanLockBadge: React.FC<{ feature: FeatureKey; className?: string }> = ({
  feature,
  className = '',
}) => {
  const { hasFeature } = useEntitlements();
  if (hasFeature(feature)) return null;

  return (
    <span
      title="Plan upgrade required"
      className={`inline-flex items-center justify-center size-4 rounded-full bg-amber-100 text-amber-700 ${className}`}
    >
      <Lock size={10} strokeWidth={2.5} />
    </span>
  );
};

export default FeatureGate;
